/**
 * ADR-0220 §3.4 as amended by Amendment 1 (owner 2026-10-29) — one initiative
 * (`/app/initiatives/:id`), built to the approved mock-up
 * https://claude.ai/artifact/Tp7Rt2aaEocEfYvLXVG7RW: an **Overview card** (a
 * status line; the goal as the lead; the description as Markdown; a facts
 * column with the timeline planned against actual, the measure and the created
 * line) over a tab strip. **Edit** turns the card into the form in place (a
 * documented UI exception — spec E14). People are edited on the **People** tab.
 *
 * Phase 1 tabs: Log (the event trail), Studies & reports, People. Phase 2 adds
 * decisions to the Log: the Record a decision row, the Decision rows with Add
 * body / Set knowledge entry / Redact. The Issues and Measure tabs are added by
 * their phases — no placeholders.
 * Every sentence in the Log, every health word and the timeline sentence are
 * served by the hub; the console draws the timeline's bars from served dates.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { MemberPicker, type MemberCandidate } from "../components/MemberPicker.js";
import { fmtDay, fmtMeasure, HEALTH_WORDS, initiativeLabel, LIFECYCLE_WORDS, type Health, type Lifecycle, type MeasureView, type TeamRef } from "./TenantInitiatives.js";
import { IssuesTable, type IssueListItem } from "./TenantIssues.js";

// ── the served shapes (hub src/initiatives/types.ts) ─────────────────────────

type PersonRef = { account_id: string; name: string };
type RelationKind = "study" | "operation" | "atlas_report";
export type AttachedRelation = { kind: RelationKind; ref: string; name: string; href: string | null; added_at: string };

export type InitiativeDetail = {
  id: string;
  name: string;
  lifecycle: Lifecycle;
  health: Health;
  measure: MeasureView | null;
  owners: Array<PersonRef & { added_at: string }>;
  teams: TeamRef[];
  open_issues: number;
  last_activity_at: string | null;
  planned_start: string | null;
  actual_start: string | null;
  planned_end: string | null;
  actual_end: string | null;
  goal: string;
  description: string | null;
  timeline: { today: string; summary: string | null };
  sponsor: PersonRef | null;
  created_by: { name: string } | null;
  created_at: string;
  created_under: { subject_id: string; title: string } | null;
  allowed_transitions: Lifecycle[];
  relations: { attached: AttachedRelation[] };
  /** Absent on a hub that predates Amendment 2 — the tab then shows the empty states. */
  related_knowledge?: RelatedKnowledge;
  cited_knowledge?: CitedKnowledge[];
  can_manage: boolean;
};

/**
 * Amendment 2 — knowledge found from the tables the linked work reads (never
 * chosen, never stored) and the entries the decisions cite. `href` is served:
 * the knowledge admin page for one who can open it, else null (E48).
 */
export type RelatedKnowledgeEntry = { name: string; title: string; through: string[]; shared: number; href: string | null };
export type RelatedKnowledge = { entries: RelatedKnowledgeEntry[]; total: number; note: string };
export type CitedKnowledge = { name: string; title: string; href: string | null; decision: { id: string; title: string; at: string } };

export type DecisionEvent = {
  kind: "decision";
  id: string;
  title: string;
  body: string | null;
  /** `href` is served: the knowledge admin page for one who can open it, else null and the title is plain text (spec E21). */
  knowledge: { ref: string; title: string; href: string | null } | null;
  author: { name: string } | null;
  at: string;
  redacted: { by: string; at: string } | null;
};
export type EventRef = { kind: string; id: string; name: string; href: string | null };
export type InitiativeEvent = { kind: "activity"; id: string; event_type: string; text: string; actor: { name: string } | null; at: string; refs?: EventRef[] } | DecisionEvent;

/** A system row's text with each served ref's name as a link (spec §3.6: the issue's title links to its page). Pure, for the tests. */
export function TextWithRefs(props: { text: string; refs?: EventRef[] }): JSX.Element {
  const refs = (props.refs ?? []).filter((r) => r.href && r.name && props.text.includes(r.name));
  if (refs.length === 0) return <>{props.text}</>;
  const r = refs[0]!;
  const at = props.text.indexOf(r.name);
  return (
    <>
      {props.text.slice(0, at)}
      <Link className="adminListName" to={r.href!}>
        {r.name}
      </Link>
      <TextWithRefs text={props.text.slice(at + r.name.length)} refs={refs.slice(1)} />
    </>
  );
}
export type KnowledgeOption = { ref: string; title: string };

const RELATION_WORDS: Record<RelationKind, string> = { study: "Study", operation: "Operation", atlas_report: "Atlas Report" };

const TIME_FMT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });

function fmtTime(iso: string): string {
  const d = new Date(iso);
  // "Oct 27, 2026, 09:40" → "Oct 27, 2026 09:40" (the mock-up's Log time).
  return Number.isNaN(d.getTime()) ? iso : TIME_FMT.format(d).replace(/, (\d\d:\d\d)$/, " $1");
}

export const NO_DESCRIPTION_TEXT = "No description yet.";

// ── the timeline's geometry (drawn here from served dates; no state derived) ─

const dayNo = (d: string) => Date.parse(`${d}T00:00:00Z`) / 86_400_000;
const MONTH_FMT = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
const MONTH_ONLY = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" });

export type TimelineGeometry = {
  planned: { left: number; width: number } | null;
  actual: { left: number; width: number } | null;
  today: number;
  scale: [string, string, string];
};

/**
 * Percent positions on one scale running from the earliest to the latest of
 * the four dates and today. The actual span runs to its end, or to today while
 * the initiative is active. Null when there is nothing to draw.
 */
export function timelineGeometry(i: Pick<InitiativeDetail, "planned_start" | "planned_end" | "actual_start" | "actual_end" | "lifecycle" | "timeline">): TimelineGeometry | null {
  const today = i.timeline.today;
  const dates = [i.planned_start, i.planned_end, i.actual_start, i.actual_end, today].filter((d): d is string => !!d);
  if (!i.planned_start && !i.planned_end && !i.actual_start) return null;
  const lo = Math.min(...dates.map(dayNo));
  const hi = Math.max(...dates.map(dayNo));
  const span = hi - lo || 1;
  const pct = (d: string) => Math.round(((dayNo(d) - lo) / span) * 1000) / 10;
  const bar = (a: string | null, b: string | null) => (a && b && dayNo(b) >= dayNo(a) ? { left: pct(a), width: Math.max(0.5, pct(b) - pct(a)) } : null);
  const actualEnd = i.actual_end ?? (i.lifecycle === "active" ? today : null);
  const mid = new Date(((lo + hi) / 2) * 86_400_000);
  return {
    planned: bar(i.planned_start, i.planned_end),
    actual: bar(i.actual_start, actualEnd),
    today: pct(today),
    scale: [MONTH_FMT.format(new Date(lo * 86_400_000)), MONTH_ONLY.format(mid), MONTH_FMT.format(new Date(hi * 86_400_000))],
  };
}

function initials(name: string): string {
  const words = name.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? words[0]?.[1] ?? "")).toUpperCase();
}

// ── the Overview card, read view ─────────────────────────────────────────────

/** The status line: lifecycle, health with its reason once, last activity. */
function StatusLine(props: { initiative: InitiativeDetail }): JSX.Element {
  const i = props.initiative;
  const h = i.health;
  const dot = h.state === null ? null : h.state === "on_track" ? "statusDotOk" : h.state === "no_measure" ? "statusDotMuted" : "statusDotWarn";
  return (
    <div className="initStatusRow">
      <span className={i.lifecycle === "active" ? "pill pillOk" : "pill pillNeutral"}>{LIFECYCLE_WORDS[i.lifecycle]}</span>
      {h.state ? (
        <span className="initHealth">
          <span className={`statusDot ${dot}`}>{HEALTH_WORDS[h.state]}</span>
          {/* The reason once — and not at all where it only restates the word (the Measure fact says "No measure declared"). */}
          {h.reason && h.state !== "no_measure" ? <span className="muted">{h.reason}</span> : null}
        </span>
      ) : null}
      <span className="initStatusAside">{i.last_activity_at ? `Last activity ${fmtDay(i.last_activity_at)}` : "No activity yet"}</span>
    </div>
  );
}

/** The timeline: planned against actual, both spans on one scale, today marked, the served sentence. */
export function TimelineFact(props: { initiative: InitiativeDetail }): JSX.Element {
  const i = props.initiative;
  const g = timelineGeometry(i);
  const end = (d: string | null) => (d ? fmtDay(d) : i.lifecycle === "active" && i.actual_start ? <span className="initPending">In progress</span> : <span className="muted">—</span>);
  return (
    <div className="initFact">
      <dt className="initLabel">Timeline</dt>
      <dd>
        <table className="initTimeTable">
          <thead>
            <tr>
              <th />
              <th>Start</th>
              <th>End</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Planned</td>
              <td>{i.planned_start ? fmtDay(i.planned_start) : <span className="muted">—</span>}</td>
              <td>{i.planned_end ? fmtDay(i.planned_end) : <span className="muted">—</span>}</td>
            </tr>
            <tr>
              <td>Actual</td>
              <td>{i.actual_start ? fmtDay(i.actual_start) : <span className="muted">—</span>}</td>
              <td>{i.actual_end ? fmtDay(i.actual_end) : end(null)}</td>
            </tr>
          </tbody>
        </table>
        {g ? (
          <>
            <div className="initTrack" role="img" aria-label={i.timeline.summary ?? "Planned and actual dates on one scale, with today marked"}>
              <div className="initTrackLane isPlanned">{g.planned ? <span className="initTrackSpan" style={{ left: `${g.planned.left}%`, width: `${g.planned.width}%` }} /> : null}</div>
              <div className="initTrackLane isActual">{g.actual ? <span className="initTrackSpan" style={{ left: `${g.actual.left}%`, width: `${g.actual.width}%` }} /> : null}</div>
              <span className="initTrackToday" style={{ left: `${g.today}%` }} />
            </div>
            <div className="initTrackScale">
              <span>{g.scale[0]}</span>
              <span>{g.scale[1]}</span>
              <span>{g.scale[2]}</span>
            </div>
            <div className="initLegend">
              <span>
                <i className="isPlanned" />
                Planned
              </span>
              <span>
                <i className="isActual" />
                Actual
              </span>
              <span>
                <i className="isToday" />
                Today
              </span>
            </div>
          </>
        ) : null}
        {i.timeline.summary ? <div className="initSub">{i.timeline.summary}</div> : null}
      </dd>
    </div>
  );
}

/** The Measure fact (mock-up Tp7Rt2aaEocEfYvLXVG7RW): the current value large, as of its run; the line from the baseline to the target; the bar. Pure, for the tests. */
export function MeasureFact(props: { measure: MeasureView | null }): JSX.Element {
  const m = props.measure;
  if (!m) {
    return (
      <div className="initFact">
        <dt className="initLabel">Measure</dt>
        <dd>
          <span className="muted">No measure declared</span>
        </dd>
      </div>
    );
  }
  const r = m.reading;
  const pct = m.progress ? Math.round(m.progress.fraction * 100) : null;
  return (
    <div className="initFact">
      <dt className="initLabel">Measure · {m.name}</dt>
      <dd>
        {r.current === null ? (
          <div className="muted">Unavailable — {r.unavailable_reason}</div>
        ) : (
          <div className="initMeasureNow">
            {fmtMeasure(r.current)}
            {r.as_of ? <small>as of {fmtDay(r.as_of)}</small> : null}
          </div>
        )}
        <div className="initSub">
          From {fmtMeasure(m.baseline)} on {fmtDay(m.baseline_as_of)} · target {fmtMeasure(m.target)}
          {pct !== null ? ` · ${pct}% of the way` : ""}
        </div>
        {pct !== null ? (
          <div className="initBar" role="img" aria-label={`${pct}% of the way from baseline to target`}>
            <span style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
          </div>
        ) : null}
      </dd>
    </div>
  );
}

/** The Overview card (Amendment 1). Pure, for the tests. */
export function InitiativeOverviewCard(props: { initiative: InitiativeDetail }): JSX.Element {
  const i = props.initiative;
  const m = i.measure;
  return (
    <article className="card initOverview" aria-label="Overview">
      <StatusLine initiative={i} />
      <div className="initBody">
        <div className="initMain">
          <section>
            <h2 className="initLabel">Goal</h2>
            <p className="initGoal">{i.goal}</p>
          </section>
          <section>
            <h2 className="initLabel">Description</h2>
            {i.description ? (
              <div className="knowledgeMarkdown initDescription">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{i.description}</ReactMarkdown>
              </div>
            ) : (
              <div className="muted" style={{ fontSize: 13 }}>
                {NO_DESCRIPTION_TEXT}
              </div>
            )}
          </section>
        </div>
        <dl className="initFacts">
          <TimelineFact initiative={i} />
          <MeasureFact measure={m} />
          <div className="initFact">
            <dt className="initLabel">Created</dt>
            <dd>
              {fmtDay(i.created_at)}
              {i.created_by ? <span className="muted"> by {i.created_by.name}</span> : null}
              {i.created_under ? <div className="initSub">Under {i.created_under.title}</div> : null}
            </dd>
          </div>
        </dl>
      </div>
    </article>
  );
}

// ── the Overview card, editing ───────────────────────────────────────────────

type EditForm = {
  name: string;
  goal: string;
  description: string;
  lifecycle: Lifecycle;
  planned_start: string;
  actual_start: string;
  planned_end: string;
  actual_end: string;
};

function toForm(i: InitiativeDetail): EditForm {
  return {
    name: i.name,
    goal: i.goal,
    description: i.description ?? "",
    lifecycle: i.lifecycle,
    planned_start: i.planned_start ?? "",
    actual_start: i.actual_start ?? "",
    planned_end: i.planned_end ?? "",
    actual_end: i.actual_end ?? "",
  };
}

/** The PATCH body: only what changed (the record's activity names exactly those fields). */
export function patchBody(form: EditForm, initial: EditForm): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (form.name.trim() !== initial.name) out.name = form.name.trim();
  if (form.goal.trim() !== initial.goal) out.goal = form.goal.trim();
  if (form.description.trim() !== initial.description.trim()) out.description = form.description.trim() || null;
  if (form.lifecycle !== initial.lifecycle) out.lifecycle = form.lifecycle;
  for (const k of ["planned_start", "actual_start", "planned_end", "actual_end"] as const) {
    if (form[k] !== initial[k]) out[k] = form[k] || null;
  }
  return out;
}

function OverviewEditCard(props: { initiative: InitiativeDetail; onCancel: () => void; onSaved: (next: InitiativeDetail) => void }): JSX.Element {
  const toast = useToast();
  const i = props.initiative;
  const initial = useMemo(() => toForm(i), [i]);
  const [form, setForm] = useState<EditForm>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const patch = (u: Partial<EditForm>) => setForm((f) => ({ ...f, ...u }));
  const validate = (f: EditForm): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!f.name.trim()) e.name = "Name is required.";
    if (!f.goal.trim()) e.goal = "Goal is required.";
    if (f.description.trim().length > 20000) e.description = "At most 20000 characters.";
    if (f.planned_start && f.planned_end && f.planned_end < f.planned_start) e.dates = "The planned end is before the planned start.";
    return e;
  };
  const check = () => setErrors(validate(form));

  async function save(): Promise<void> {
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    setSaveError(null);
    try {
      const r = await apiFetch<{ initiative: InitiativeDetail }>(`/api/tenant/initiatives/${encodeURIComponent(i.id)}`, {
        method: "PATCH",
        body: patchBody(form, initial),
      });
      toast.push({ kind: "success", message: "Saved." });
      props.onSaved(r.initiative);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const danger = { color: "var(--danger-text)" };
  const lifecycles: Lifecycle[] = [i.lifecycle, ...i.allowed_transitions];
  return (
    <article className="card initOverview" aria-label="Edit the initiative">
      <div className="initStatusRow">
        <label className="field" style={{ gap: 4 }}>
          <span className="initLabel" style={{ margin: 0 }}>
            Lifecycle
          </span>
          <select className="select" style={{ width: "auto" }} value={form.lifecycle} onChange={(e) => patch({ lifecycle: e.target.value as Lifecycle })}>
            {lifecycles.map((l) => (
              <option key={l} value={l}>
                {LIFECYCLE_WORDS[l]}
              </option>
            ))}
          </select>
        </label>
        <span className="initStatusAside">Health is worked out from the record; it is not edited. People are edited on the People tab.</span>
      </div>
      <div className="initBody">
        <div className="initMain initEditGrid">
          <label className="field">
            <span className="initLabel" style={{ margin: 0 }}>
              Name
            </span>
            <input className="input" value={form.name} onChange={(e) => patch({ name: e.target.value })} onBlur={check} />
            {errors.name ? <div className="help" style={danger}>{errors.name}</div> : null}
          </label>
          <label className="field">
            <span className="initLabel" style={{ margin: 0 }}>
              Goal
            </span>
            <textarea className="input" rows={3} value={form.goal} onChange={(e) => patch({ goal: e.target.value })} onBlur={check} />
            <div className="help" style={errors.goal ? danger : undefined}>
              {errors.goal ?? "One or two sentences: the result the business commits to, and how you will know it happened."}
            </div>
          </label>
          <label className="field">
            <span className="initLabel" style={{ margin: 0 }}>
              Description
            </span>
            <textarea className="input initMarkdown" value={form.description} onChange={(e) => patch({ description: e.target.value })} onBlur={check} />
            <div className="help" style={errors.description ? danger : undefined}>
              {errors.description ?? "Markdown: headings, lists, links. Background, scope, approach — what the team should read before the meeting."}
            </div>
          </label>
        </div>
        <div className="initFacts initEditGrid">
          <span className="initLabel" style={{ margin: 0 }}>
            Timeline
          </span>
          <div className="initEditPair">
            <label className="field">
              <span className="label">Planned start</span>
              <input className="input" type="date" value={form.planned_start} onChange={(e) => patch({ planned_start: e.target.value })} onBlur={check} />
            </label>
            <label className="field">
              <span className="label">Planned end</span>
              <input className="input" type="date" value={form.planned_end} onChange={(e) => patch({ planned_end: e.target.value })} onBlur={check} />
            </label>
          </div>
          <div className="initEditPair">
            <label className="field">
              <span className="label">Actual start</span>
              <input className="input" type="date" value={form.actual_start} onChange={(e) => patch({ actual_start: e.target.value })} />
              <div className="help">Set when it went active</div>
            </label>
            <label className="field">
              <span className="label">Actual end</span>
              <input className="input" type="date" value={form.actual_end} onChange={(e) => patch({ actual_end: e.target.value })} />
              <div className="help">Set when it is delivered</div>
            </label>
          </div>
          {errors.dates ? <div className="help" style={danger}>{errors.dates}</div> : null}
        </div>
      </div>
      {saveError ? (
        <div className="help" style={{ ...danger, padding: "0 20px 8px" }}>
          {saveError}
        </div>
      ) : null}
      <div className="initEditFooter">
        <button type="button" className="btn btnSm" disabled={busy} onClick={props.onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btnSm btnPrimary" disabled={!dirty || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </article>
  );
}

// ── Log ──────────────────────────────────────────────────────────────────────

export const LOG_EMPTY_TEXT = "Nothing recorded yet.";

/** A recorded decision takes the top of the trail at once — no reload (impl plan Phase 2 step 6). Pure, for the tests. */
export function withRecorded(events: InitiativeEvent[], decision: DecisionEvent): InitiativeEvent[] {
  return [decision, ...events.filter((e) => e.id !== decision.id)];
}

/** A changed decision (body added, knowledge set, redacted) replaces its row in place. */
export function withDecision(events: InitiativeEvent[], decision: DecisionEvent): InitiativeEvent[] {
  return events.map((e) => (e.kind === "decision" && e.id === decision.id ? decision : e));
}

type DecisionActions = {
  /** Owners and admins: the first body, the knowledge entry. */
  canManage: boolean;
  /** A workspace admin: redaction. */
  isAdmin: boolean;
  onAddBody: (d: DecisionEvent, body: string) => Promise<void>;
  onSetKnowledge: (d: DecisionEvent, ref: string | null) => Promise<void>;
  onRedact: (d: DecisionEvent) => Promise<void>;
  /** The accepted entries the viewer may link, loaded once when first asked for. */
  loadKnowledgeOptions: () => Promise<KnowledgeOption[]>;
};

/** One decision row: the pill, the title, the body, "became <entry>", the author; a redacted one says who and when. */
export function DecisionRow(props: { decision: DecisionEvent; actions?: DecisionActions }): JSX.Element {
  const d = props.decision;
  const a = props.actions;
  const [mode, setMode] = useState<"none" | "body" | "knowledge">("none");
  const [body, setBody] = useState("");
  const [options, setOptions] = useState<KnowledgeOption[] | null>(null);
  const [ref, setRef] = useState<string>(d.knowledge?.ref ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function openKnowledge(): Promise<void> {
    setMode("knowledge");
    setErr(null);
    if (options !== null || !a) return;
    try {
      setOptions(await a.loadKnowledgeOptions());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }
  async function run(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      setMode("none");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (d.redacted) {
    return (
      <div>
        <span className="pill">Decision</span> <strong>Redacted</strong>
        <div className="muted" style={{ fontSize: 12 }}>
          Redacted by {d.redacted.by} on {fmtDay(d.redacted.at)}
        </div>
      </div>
    );
  }
  return (
    <div>
      <span className="pill">Decision</span> <strong>{d.title}</strong>
      {d.author ? <span className="aiActivityActor"> {d.author.name}</span> : null}
      {d.body ? <div className="aiActivityCommentBody">{d.body}</div> : null}
      {d.knowledge ? (
        <div className="muted" style={{ fontSize: 12 }}>
          became {d.knowledge.href ? <Link to={d.knowledge.href}>{d.knowledge.title}</Link> : <span>{d.knowledge.title}</span>}
        </div>
      ) : null}
      {a && (a.canManage || a.isAdmin) && mode === "none" ? (
        <div className="adminListActions" style={{ marginTop: 4 }}>
          {a.canManage && d.body === null ? (
            <button type="button" className="adminListAction" onClick={() => setMode("body")}>
              Add body
            </button>
          ) : null}
          {a.canManage ? (
            <button type="button" className="adminListAction" onClick={() => void openKnowledge()}>
              {d.knowledge ? "Change knowledge entry" : "Set knowledge entry"}
            </button>
          ) : null}
          {a.isAdmin ? (
            <button type="button" className="adminListAction isDanger" onClick={() => void a.onRedact(d)}>
              Redact
            </button>
          ) : null}
        </div>
      ) : null}
      {a && mode === "body" ? (
        <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
          <textarea className="input" rows={3} value={body} placeholder="Why, and what it means — written once." maxLength={10000} onChange={(e) => setBody(e.target.value)} />
          <div className="actions">
            <button type="button" className="btn btnSm" disabled={busy} onClick={() => setMode("none")}>
              Cancel
            </button>
            <button type="button" className="btn btnSm btnPrimary" disabled={busy || !body.trim()} onClick={() => void run(() => a.onAddBody(d, body.trim()))}>
              {busy ? "Saving…" : "Save body"}
            </button>
          </div>
        </div>
      ) : null}
      {a && mode === "knowledge" ? (
        <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
          {options === null && !err ? (
            <span className="muted">Loading entries…</span>
          ) : (
            <select className="select" value={ref} onChange={(e) => setRef(e.target.value)}>
              <option value="">None</option>
              {(options ?? []).map((o) => (
                <option key={o.ref} value={o.ref}>
                  {o.ref} — {o.title}
                </option>
              ))}
            </select>
          )}
          <div className="actions">
            <button type="button" className="btn btnSm" disabled={busy} onClick={() => setMode("none")}>
              Cancel
            </button>
            <button type="button" className="btn btnSm btnPrimary" disabled={busy || options === null} onClick={() => void run(() => a.onSetKnowledge(d, ref || null))}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : null}
      {err ? (
        <div className="help" style={{ color: "var(--danger-text)" }}>
          {err}
        </div>
      ) : null}
    </div>
  );
}

/** The event trail in the action-item activity markup (spec §3.4 Log; E5). Pure without `actions`, for the tests. */
export function LogList(props: { events: InitiativeEvent[]; actions?: DecisionActions }): JSX.Element {
  if (props.events.length === 0) return <div className="muted">{LOG_EMPTY_TEXT}</div>;
  return (
    <ul className="aiActivityList" style={{ margin: 0, paddingLeft: 0, listStyle: "none", borderTop: "1px solid var(--border)" }}>
      {props.events.map((e) => (
        <li key={e.id} className="aiActivityRow">
          <span className="aiActivityTime">{fmtTime(e.at)}</span>
          <div className="aiActivityBody">
            {e.kind === "activity" ? (
              <span className="aiActivitySystemText">
                <TextWithRefs text={e.text} refs={e.refs} />
              </span>
            ) : (
              <DecisionRow decision={e} actions={props.actions} />
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export const DECISION_PLACEHOLDER = "Record a decision — what was settled, in one line";

/** The Record a decision row (owners and admins). Pure, for the tests; `onRecord` resolves when the hub has it. */
export function RecordDecisionRow(props: { onRecord: (title: string) => Promise<void> }): JSX.Element {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function record(): Promise<void> {
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    setErr(null);
    try {
      await props.onRecord(t);
      setTitle("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div style={{ display: "grid", gap: 4, marginBottom: 10 }}>
      <div className="inputWithButton">
        <input
          className="input"
          type="text"
          value={title}
          maxLength={200}
          placeholder={DECISION_PLACEHOLDER}
          aria-label="Record a decision"
          disabled={busy}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void record();
          }}
        />
        <button type="button" className="btn btnSm btnPrimary" disabled={busy || !title.trim()} onClick={() => void record()}>
          {busy ? "Recording…" : "Record"}
        </button>
      </div>
      {err ? (
        <div className="help" style={{ color: "var(--danger-text)" }}>
          {err}
        </div>
      ) : null}
    </div>
  );
}

function LogTab(props: { initiativeId: string; refreshKey: number; canManage: boolean; isAdmin: boolean; onChanged: () => void }): JSX.Element {
  const [events, setEvents] = useState<InitiativeEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { confirmWithReason } = useConfirm();
  const base = `/api/tenant/initiatives/${encodeURIComponent(props.initiativeId)}/events`;
  const decisions = `/api/tenant/initiatives/${encodeURIComponent(props.initiativeId)}/decisions`;
  const knowledgeOptions = useMemo(() => {
    let cached: Promise<KnowledgeOption[]> | null = null;
    return () =>
      (cached ??= apiFetch<{ entries: KnowledgeOption[] }>(`/api/tenant/initiatives/${encodeURIComponent(props.initiativeId)}/knowledge-entries`).then((r) => r.entries));
  }, [props.initiativeId]);

  async function record(title: string): Promise<void> {
    const r = await apiFetch<{ decision: DecisionEvent }>(decisions, { method: "POST", body: { title } });
    setEvents((prev) => withRecorded(prev, r.decision));
    toast.push({ kind: "success", message: "Decision recorded." });
    props.onChanged();
  }
  async function patch(d: DecisionEvent, body: Record<string, unknown>): Promise<void> {
    const r = await apiFetch<{ decision: DecisionEvent }>(`${decisions}/${encodeURIComponent(d.id)}`, { method: "PATCH", body });
    setEvents((prev) => withDecision(prev, r.decision));
    toast.push({ kind: "success", message: "Saved." });
    props.onChanged();
  }
  async function redact(d: DecisionEvent): Promise<void> {
    const reason = await confirmWithReason({
      title: `Redact the decision “${d.title}”?`,
      detail: "Its title and body are replaced by “Redacted” on every surface, including the status report. The row, its time and its author stay. This cannot be undone.",
      confirmLabel: "Redact",
      destructive: true,
      reasonField: { label: "Reason", placeholder: "e.g. names a customer contact" },
    });
    if (reason === null) return;
    try {
      const r = await apiFetch<{ decision: DecisionEvent }>(`${decisions}/${encodeURIComponent(d.id)}/redact`, { method: "POST", body: { reason } });
      setEvents((prev) => withDecision(prev, r.decision));
      toast.push({ kind: "success", message: "Decision redacted." });
      props.onChanged();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }
  const actions: DecisionActions = {
    canManage: props.canManage,
    isAdmin: props.isAdmin,
    onAddBody: (d, body) => patch(d, { body }),
    onSetKnowledge: (d, ref) => patch(d, { knowledge_ref: ref }),
    onRedact: redact,
    loadKnowledgeOptions: knowledgeOptions,
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<{ events: InitiativeEvent[]; next_cursor: string | null }>(`${base}?limit=100`)
      .then((r) => {
        if (cancelled) return;
        setEvents(r.events);
        setCursor(r.next_cursor);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [base, props.refreshKey]);

  async function loadMore(): Promise<void> {
    if (!cursor) return;
    setMore(true);
    try {
      const r = await apiFetch<{ events: InitiativeEvent[]; next_cursor: string | null }>(`${base}?limit=100&before=${encodeURIComponent(cursor)}`);
      setEvents((prev) => [...prev, ...r.events]);
      setCursor(r.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setMore(false);
    }
  }

  return (
    <div className="card">
      {props.canManage ? <RecordDecisionRow onRecord={record} /> : null}
      {error ? <ErrorState detail={error} /> : null}
      {!error && loading ? <LoadingState /> : null}
      {!error && !loading ? <LogList events={events} actions={actions} /> : null}
      {!error && !loading && cursor ? (
        <div className="actions" style={{ marginTop: 10 }}>
          <button type="button" className="btn" disabled={more} onClick={() => void loadMore()}>
            {more ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ── Issues (ADR-0221 §3.3) ───────────────────────────────────────────────────

export const ISSUES_EMPTY_TEXT = "No open issues.";
export const ISSUES_EMPTY_CLOSED_TEXT = "No issues yet.";

/** The initiative's Issues tab: the same table scoped to it, with Resolution instead of Initiative; Show closed and search; All issues and Raise issue at the right. */
function IssuesTab(props: { initiativeId: string; canRaise: boolean; refreshKey: number }): JSX.Element {
  const [items, setItems] = useState<IssueListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");
  const base = `/api/tenant/initiatives/${encodeURIComponent(props.initiativeId)}/issues`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const p = new URLSearchParams();
    if (showClosed) p.set("show_closed", "1");
    if (applied.trim()) p.set("q", applied.trim());
    apiFetch<{ issues: IssueListItem[] }>(`${base}${p.toString() ? `?${p}` : ""}`)
      .then((r) => !cancelled && setItems(r.issues))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [base, showClosed, applied, props.refreshKey]);

  return (
    <div className="card adminList">
      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="toolbarGroup">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
            Show closed
          </label>
          <input
            className="input"
            type="search"
            placeholder="Search issues"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onBlur={() => setApplied(q)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setApplied(q);
            }}
            style={{ width: 220, fontSize: 13 }}
          />
        </div>
        <div className="toolbarGroup" style={{ marginLeft: "auto" }}>
          <Link className="muted" to={`/app/issues?initiative_ids_csv=${encodeURIComponent(props.initiativeId)}`} style={{ fontSize: 13, textDecoration: "underline" }}>
            All issues
          </Link>
          {props.canRaise ? (
            <Link className="btn btnSm btnPrimary" to={`/app/issues/new?initiative=${encodeURIComponent(props.initiativeId)}`}>
              + Raise issue
            </Link>
          ) : null}
        </div>
      </div>
      {error ? <ErrorState detail={error} /> : null}
      {!error && loading ? <LoadingState /> : null}
      {!error && !loading && items.length === 0 ? <div className="adminListEmpty">{showClosed || applied ? ISSUES_EMPTY_CLOSED_TEXT : ISSUES_EMPTY_TEXT}</div> : null}
      {!error && !loading && items.length > 0 ? (
        <div className="tableWrap">
          <IssuesTable items={items} initiativeColumn={false} />
        </div>
      ) : null}
    </div>
  );
}

// ── Studies & reports ────────────────────────────────────────────────────────

export const RELATIONS_EMPTY_TEXT = "Nothing added yet.";

/** The relations table (spec §3.4). Pure, for the tests; `onRemove` absent = read-only. */
export function RelationsTable(props: { attached: AttachedRelation[]; onRemove?: (r: AttachedRelation) => void; busy?: boolean }): JSX.Element {
  if (props.attached.length === 0) return <div className="adminListEmpty">{RELATIONS_EMPTY_TEXT}</div>;
  return (
    <div className="tableWrap">
      <table className="table">
        <thead>
          <tr>
            <th>Kind</th>
            <th>Name</th>
            <th>Added</th>
            {props.onRemove ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {props.attached.map((r) => {
            const label =
              r.kind === "study" ? (
                <>
                  <span className="mono">{r.ref}</span>
                  {r.name ? ` — ${r.name}` : ""}
                </>
              ) : (
                r.name
              );
            return (
              <tr key={`${r.kind}:${r.ref}`}>
                <td>
                  <span className="pill pillNeutral">{RELATION_WORDS[r.kind]}</span>
                </td>
                <td>
                  {r.href ? (
                    <Link className="adminListName" to={r.href}>
                      {label}
                    </Link>
                  ) : (
                    label
                  )}
                </td>
                <td>{fmtDay(r.added_at)}</td>
                {props.onRemove ? (
                  <td className="adminListActions">
                    <button type="button" className="adminListAction" disabled={props.busy} onClick={() => props.onRemove!(r)}>
                      Remove
                    </button>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type Option = { ref: string; label: string };

// ── Measure (Phase 3; spec §3.4) ─────────────────────────────────────────────

export type MeasureReadingRow = { run_at: string; value: number; change: number | null };
type MeasureSources = { operations: Array<{ id: string; name: string }>; steps?: Array<{ node_id: string; label: string; columns: Array<{ name: string; type: string }> }> };

export const PICKS: Array<{ value: MeasureView["pick"]; label: string }> = [
  { value: "last_row", label: "Last row" },
  { value: "sum", label: "Sum" },
  { value: "avg", label: "Average" },
  { value: "max", label: "Max" },
  { value: "min", label: "Min" },
];
export const DIRECTIONS: Array<{ value: MeasureView["direction"]; label: string }> = [
  { value: "lower_is_better", label: "Lower is better" },
  { value: "higher_is_better", label: "Higher is better" },
];
const PICK_LABEL = (p: string) => PICKS.find((x) => x.value === p)?.label.toLowerCase() ?? p;
export const NO_MEASURE_TEXT = "No measure declared. A measure is a numeric column of one step's table in an operation — never a typed-in number.";
export const NO_READINGS_TEXT = "No readings yet. Each completed run of the operation records one.";

/** A change as the readings table shows it: signed, "—" for the first reading. Pure, for the tests. */
export function signedChange(change: number | null): string {
  if (change === null) return "—";
  if (change === 0) return "0";
  return `${change > 0 ? "+" : "−"}${fmtMeasure(Math.abs(change))}`;
}

/** The five fields, the progress line and the readings (spec §3.4 Measure). Pure, for the tests. */
export function MeasureView_(props: { measure: MeasureView; plannedEnd: string | null; readings: MeasureReadingRow[] }): JSX.Element {
  const m = props.measure;
  const r = m.reading;
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10, marginBottom: 12 }}>
        <div className="field">
          <span className="label">Source</span>
          <div style={{ fontSize: 13 }}>
            {m.operation ? m.operation.href ? <Link className="adminListName" to={m.operation.href}>{m.operation.name}</Link> : <span>{m.operation.name}</span> : <span className="muted">A deleted operation</span>}
            <span className="muted"> · {m.step_label} · {PICK_LABEL(m.pick)}</span>
          </div>
        </div>
        <div className="field">
          <span className="label">Direction</span>
          <div style={{ fontSize: 13 }}>{DIRECTIONS.find((d) => d.value === m.direction)?.label}</div>
        </div>
        <div className="field">
          <span className="label">Baseline</span>
          <div style={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
            {fmtMeasure(m.baseline)} <span className="muted">on {fmtDay(m.baseline_as_of)}</span>
          </div>
        </div>
        <div className="field">
          <span className="label">Now</span>
          <div style={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
            {r.current === null ? (
              <span className="muted">Unavailable — {r.unavailable_reason}</span>
            ) : (
              <>
                {fmtMeasure(r.current)} {r.as_of ? <span className="muted">as of {fmtDay(r.as_of)}</span> : null}
              </>
            )}
          </div>
        </div>
        <div className="field">
          <span className="label">Target</span>
          <div style={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
            {fmtMeasure(m.target)} {props.plannedEnd ? <span className="muted">by {fmtDay(props.plannedEnd)}</span> : null}
          </div>
        </div>
      </div>
      {m.progress ? (
        <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          {m.progress.sentence}
        </div>
      ) : null}
      {props.readings.length === 0 ? (
        <div className="muted" style={{ fontSize: 13 }}>
          {NO_READINGS_TEXT}
        </div>
      ) : (
        <div className="tableWrap" style={{ maxWidth: 560 }}>
          <table className="table tableCompact">
            <thead>
              <tr>
                <th>Run</th>
                <th>Value</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {props.readings.map((x) => (
                <tr key={x.run_at}>
                  <td>{fmtDay(x.run_at)}</td>
                  <td style={{ fontVariantNumeric: "tabular-nums" }}>{fmtMeasure(x.value)}</td>
                  <td style={{ fontVariantNumeric: "tabular-nums" }}>{signedChange(x.change)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

type MeasureForm = { operation_id: string; node_id: string; column: string; pick: MeasureView["pick"]; direction: MeasureView["direction"] | ""; baseline: string; baseline_as_of: string; target: string };

/** The Edit measure checks, on Save. Pure, for the tests. */
export function validateMeasure(f: MeasureForm): Partial<Record<keyof MeasureForm, string>> {
  const errs: Partial<Record<keyof MeasureForm, string>> = {};
  if (!f.operation_id) errs.operation_id = "Choose the operation.";
  if (!f.node_id) errs.node_id = "Choose the step.";
  if (!f.column) errs.column = "Choose a numeric column.";
  if (!f.direction) errs.direction = "Choose which way is better.";
  if (f.baseline.trim() === "" || !Number.isFinite(Number(f.baseline))) errs.baseline = "The baseline is a number.";
  if (!f.baseline_as_of) errs.baseline_as_of = "The date the baseline describes.";
  if (f.target.trim() === "" || !Number.isFinite(Number(f.target))) errs.target = "The target is a number.";
  return errs;
}

function MeasureTab(props: { initiative: InitiativeDetail; onChanged: (next: InitiativeDetail) => void }): JSX.Element {
  const i = props.initiative;
  const m = i.measure;
  const toast = useToast();
  const { confirm } = useConfirm();
  const base = `/api/tenant/initiatives/${encodeURIComponent(i.id)}`;
  const [readings, setReadings] = useState<MeasureReadingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [sources, setSources] = useState<MeasureSources | null>(null);
  const [form, setForm] = useState<MeasureForm>(() => formOf(m));
  const [errs, setErrs] = useState<Partial<Record<keyof MeasureForm, string>>>({});
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function formOf(x: MeasureView | null): MeasureForm {
    return x
      ? { operation_id: x.operation_id ?? "", node_id: x.node_id, column: x.column, pick: x.pick, direction: x.direction, baseline: String(x.baseline), baseline_as_of: x.baseline_as_of, target: String(x.target) }
      : { operation_id: "", node_id: "", column: "", pick: "last_row", direction: "", baseline: "", baseline_as_of: "", target: "" };
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (m ? apiFetch<{ readings: MeasureReadingRow[] }>(`${base}/measure-readings?limit=12`) : Promise.resolve({ readings: [] }))
      .then((r) => !cancelled && setReadings(r.readings))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [base, m?.operation_id, m?.node_id, m?.column, m?.pick]);

  async function loadSources(operationId: string): Promise<void> {
    try {
      setSources(await apiFetch<MeasureSources>(`${base}/measure-sources${operationId ? `?operation_id=${encodeURIComponent(operationId)}` : ""}`));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  }

  function startEdit(): void {
    setForm(formOf(m));
    setErrs({});
    setSaveError(null);
    setEditing(true);
    void loadSources(m?.operation_id ?? "");
  }

  function chooseOperation(id: string): void {
    setForm((f) => ({ ...f, operation_id: id, node_id: "", column: "" }));
    void loadSources(id);
  }

  async function save(): Promise<void> {
    const v = validateMeasure(form);
    setErrs(v);
    if (Object.keys(v).length > 0) return;
    setBusy(true);
    setSaveError(null);
    try {
      const r = await apiFetch<{ initiative: InitiativeDetail }>(`${base}/measure`, {
        method: "PUT",
        body: { operation_id: form.operation_id, node_id: form.node_id, column: form.column, pick: form.pick, direction: form.direction, baseline: Number(form.baseline), baseline_as_of: form.baseline_as_of, target: Number(form.target) },
      });
      props.onChanged(r.initiative);
      setEditing(false);
      toast.push({ kind: "success", message: "Measure saved." });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function clearMeasure(): Promise<void> {
    const ok = await confirm({
      title: "Clear the measure?",
      detail: "The initiative stops reading its operation and health returns to No measure. The readings already recorded stay in the Log.",
      confirmLabel: "Clear measure",
      destructive: true,
    });
    if (!ok) return;
    try {
      const r = await apiFetch<{ initiative: InitiativeDetail }>(`${base}/measure`, { method: "PUT", body: {} });
      props.onChanged(r.initiative);
      setEditing(false);
      toast.push({ kind: "success", message: "Measure cleared." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  const steps = sources?.steps ?? [];
  const step = steps.find((s) => s.node_id === form.node_id);
  const err = (k: keyof MeasureForm) => (errs[k] ? <div className="help" style={{ color: "var(--danger-text)" }}>{errs[k]}</div> : null);

  return (
    <div className="card">
      {error ? <ErrorState detail={error} /> : null}
      {!error && loading ? <LoadingState /> : null}
      {!error && !loading && !editing ? (
        <>
          {m ? <MeasureView_ measure={m} plannedEnd={i.planned_end} readings={readings} /> : <div className="muted" style={{ fontSize: 13 }}>{NO_MEASURE_TEXT}</div>}
          {i.can_manage ? (
            <div className="actions" style={{ marginTop: 12, alignItems: "center" }}>
              <button type="button" className="btn btnSm" onClick={startEdit}>
                {m ? "Edit measure" : "Declare a measure"}
              </button>
              {m ? (
                <button type="button" className="adminListAction isDanger" onClick={() => void clearMeasure()}>
                  Clear measure
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
      {editing ? (
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
            <label className="field">
              <span className="label">Operation</span>
              <select className="select" value={form.operation_id} onChange={(e) => chooseOperation(e.target.value)}>
                <option value="">Select…</option>
                {(sources?.operations ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              {err("operation_id")}
            </label>
            <label className="field">
              <span className="label">Step</span>
              <select className="select" value={form.node_id} disabled={!form.operation_id} onChange={(e) => setForm((f) => ({ ...f, node_id: e.target.value, column: "" }))}>
                <option value="">Select…</option>
                {steps.map((s) => (
                  <option key={s.node_id} value={s.node_id}>
                    {s.label}
                    {s.columns.length === 0 ? " (no numeric columns yet — run it first)" : ""}
                  </option>
                ))}
              </select>
              {err("node_id")}
            </label>
            <label className="field">
              <span className="label">Column</span>
              <select className="select" value={form.column} disabled={!step} onChange={(e) => setForm((f) => ({ ...f, column: e.target.value }))}>
                <option value="">Select…</option>
                {(step?.columns ?? []).map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              {err("column")}
            </label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10 }}>
            <label className="field">
              <span className="label">Pick</span>
              <select className="select" value={form.pick} onChange={(e) => setForm((f) => ({ ...f, pick: e.target.value as MeasureView["pick"] }))}>
                {PICKS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="label">Direction</span>
              <select className="select" value={form.direction} onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value as MeasureForm["direction"] }))}>
                <option value="">Select…</option>
                {DIRECTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
              {err("direction")}
            </label>
            <label className="field">
              <span className="label">Baseline</span>
              <input className="input" inputMode="decimal" value={form.baseline} onChange={(e) => setForm((f) => ({ ...f, baseline: e.target.value }))} />
              {err("baseline")}
            </label>
            <label className="field">
              <span className="label">Baseline as of</span>
              <input className="input" type="date" value={form.baseline_as_of} onChange={(e) => setForm((f) => ({ ...f, baseline_as_of: e.target.value }))} />
              {err("baseline_as_of")}
            </label>
            <label className="field">
              <span className="label">Target</span>
              <input className="input" inputMode="decimal" value={form.target} onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))} />
              {err("target")}
            </label>
          </div>
          {saveError ? <div className="help" style={{ color: "var(--danger-text)" }}>{saveError}</div> : null}
          <div className="actions">
            <button type="button" className="btn btnSm" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="button" className="btn btnSm btnPrimary" disabled={busy} onClick={() => void save()}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export const LINKED_NOTE = "Chosen by the owners. These are the studies, operations and reports this initiative uses.";
export const RELATED_KNOWLEDGE_EMPTY = "No linked operation or study reads any table yet.";
export const CITED_NOTE = "Entries a decision on this initiative became. These stay as history: each is tied to the decision and its date.";
export const CITED_EMPTY = "No decision has cited a knowledge entry.";
/** What the tab shows when the hub serves no related knowledge (one that predates Amendment 2): the empty state, without a note. */
export const NO_RELATED_KNOWLEDGE: RelatedKnowledge = { entries: [], total: 0, note: "" };

/** The entry as the mock-up shows it: the description (a link when one is served), the name under it in small monospace. */
function KnowledgeCell(props: { title: string; name: string; href: string | null }): JSX.Element {
  return (
    <td>
      {props.href ? (
        <Link className="adminListName" to={props.href}>
          {props.title}
        </Link>
      ) : (
        props.title
      )}
      <span className="initEntryName">{props.name}</span>
    </td>
  );
}

/** Knowledge related by data (Amendment 2). Pure, for the tests; `onShowAll` loads the full list in place. */
export function RelatedKnowledgeSection(props: { related: RelatedKnowledge | undefined; onShowAll?: () => void; busy?: boolean }): JSX.Element {
  const r = props.related ?? NO_RELATED_KNOWLEDGE;
  const shown = r.entries.length;
  return (
    <section className="initSection">
      <div className="initSectionHead">
        <h3>Knowledge related by data</h3>
        <span className="muted">{shown < r.total ? `${shown} of ${r.total}` : r.total}</span>
      </div>
      {r.note ? <p className="initSectionNote">{r.note}</p> : null}
      {shown === 0 ? (
        <div className="adminListEmpty">{RELATED_KNOWLEDGE_EMPTY}</div>
      ) : (
        <div className="tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>Knowledge</th>
                <th>Through</th>
                <th>Tables</th>
              </tr>
            </thead>
            <tbody>
              {r.entries.map((e) => (
                <tr key={e.name}>
                  <KnowledgeCell title={e.title} name={e.name} href={e.href} />
                  <td>
                    <div className="initThrough">
                      {e.through.map((o) => (
                        <span key={o} className="mono">
                          {o}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="initTabular">{e.shared}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {shown < r.total && props.onShowAll ? (
        <div className="initMore">
          <button type="button" className="linkBtn" disabled={props.busy} onClick={props.onShowAll}>
            {props.busy ? "Loading…" : `Show all ${r.total}`}
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** Knowledge cited by decisions (Amendment 2). Pure, for the tests. */
export function CitedKnowledgeSection(props: { cited: CitedKnowledge[] | undefined }): JSX.Element {
  const cited = props.cited ?? [];
  return (
    <section className="initSection">
      <div className="initSectionHead">
        <h3>Knowledge cited by decisions</h3>
        <span className="muted">{cited.length}</span>
      </div>
      <p className="initSectionNote">{CITED_NOTE}</p>
      {cited.length === 0 ? (
        <div className="adminListEmpty">{CITED_EMPTY}</div>
      ) : (
        <div className="tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>Knowledge</th>
                <th>Decision</th>
                <th>Decided</th>
              </tr>
            </thead>
            <tbody>
              {cited.map((c) => (
                <tr key={c.decision.id}>
                  <KnowledgeCell title={c.title} name={c.name} href={c.href} />
                  <td>{c.decision.title}</td>
                  <td>{fmtDay(c.decision.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The Studies & reports tab. Exported for the tests. */
export function RelationsTab(props: { initiative: InitiativeDetail; onChanged: (attached: AttachedRelation[]) => void }): JSX.Element {
  const toast = useToast();
  const i = props.initiative;
  const [kind, setKind] = useState<RelationKind>("study");
  const [options, setOptions] = useState<Record<RelationKind, Option[]> | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  // Show all: the full list (up to 200) replaces the first 12 in place until the detail changes.
  const [allRelated, setAllRelated] = useState<RelatedKnowledge | null>(null);
  const [loadingAll, setLoadingAll] = useState(false);
  useEffect(() => setAllRelated(null), [i.related_knowledge]);

  async function showAll(): Promise<void> {
    setLoadingAll(true);
    try {
      const r = await apiFetch<{ related_knowledge: RelatedKnowledge }>(`/api/tenant/initiatives/${encodeURIComponent(i.id)}/related-knowledge?all=true`);
      setAllRelated(r.related_knowledge);
    } catch (e) {
      // A hub without the route (one that predates Amendment 2) answers 404: keep the list shown and drop Show all.
      if (e instanceof ApiRequestError && e.status === 404) {
        const shown = i.related_knowledge ?? NO_RELATED_KNOWLEDGE;
        setAllRelated({ ...shown, total: shown.entries.length });
        return;
      }
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setLoadingAll(false);
    }
  }

  useEffect(() => {
    if (!i.can_manage) return;
    void Promise.all([
      apiFetch<{ studies: Array<{ id: string; title: string }> }>("/api/tenant/studies"),
      apiFetch<{ operations: Array<{ id: string; name: string }> }>("/api/tenant/operations"),
      apiFetch<{ reports: Array<{ report_id: string; name: string }> }>("/api/tenant/atlas-reports"),
    ])
      .then(([s, o, r]) =>
        setOptions({
          study: s.studies.map((x) => ({ ref: x.id, label: `${x.id} — ${x.title}` })).sort((a, b) => a.ref.localeCompare(b.ref, undefined, { numeric: true })),
          operation: o.operations.map((x) => ({ ref: x.id, label: x.name })).sort((a, b) => a.label.localeCompare(b.label)),
          atlas_report: r.reports.map((x) => ({ ref: x.report_id, label: x.name })).sort((a, b) => a.label.localeCompare(b.label)),
        })
      )
      .catch((e) => setOptionsError(e instanceof Error ? e.message : String(e)));
  }, [i.can_manage]);

  const attachedKeys = new Set(i.relations.attached.map((r) => `${r.kind}:${r.ref.toLowerCase()}`));
  const choices = (options?.[kind] ?? []).filter((o) => !attachedKeys.has(`${kind}:${o.ref.toLowerCase()}`));

  async function change(body: { add?: Array<{ kind: RelationKind; ref: string }>; remove?: Array<{ kind: RelationKind; ref: string }> }): Promise<void> {
    setBusy(true);
    try {
      const r = await apiFetch<{ relations: { attached: AttachedRelation[] } }>(`/api/tenant/initiatives/${encodeURIComponent(i.id)}/relations`, {
        method: "PUT",
        body,
      });
      props.onChanged(r.relations.attached);
      setRef("");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card adminList">
      <section className="initSection">
        <div className="initSectionHead">
          <h3>Linked</h3>
          <span className="muted">{i.relations.attached.length}</span>
        </div>
        <p className="initSectionNote">{LINKED_NOTE}</p>
        <RelationsTable attached={i.relations.attached} busy={busy} onRemove={i.can_manage ? (r) => void change({ remove: [{ kind: r.kind, ref: r.ref }] }) : undefined} />
        {i.can_manage ? (
          <div className="inputRow" style={{ marginTop: 10, alignItems: "end" }}>
            <label className="field">
              <span className="label">Kind</span>
              <select
                className="select"
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as RelationKind);
                  setRef("");
                }}
              >
                <option value="study">Study</option>
                <option value="operation">Operation</option>
                <option value="atlas_report">Atlas Report</option>
              </select>
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="label">{RELATION_WORDS[kind]}</span>
              <select className="select" value={ref} disabled={!options} onChange={(e) => setRef(e.target.value)}>
                <option value="">{options ? (choices.length === 0 ? "Nothing more to add" : "Choose…") : optionsError ? "Unavailable" : "Loading…"}</option>
                {choices.map((o) => (
                  <option key={o.ref} value={o.ref}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="actions">
              <button type="button" className="btn btnSm" disabled={!ref || busy} onClick={() => void change({ add: [{ kind, ref }] })}>
                Add
              </button>
            </div>
          </div>
        ) : null}
      </section>
      <RelatedKnowledgeSection related={allRelated ?? i.related_knowledge} onShowAll={() => void showAll()} busy={loadingAll} />
      <CitedKnowledgeSection cited={i.cited_knowledge} />
    </div>
  );
}

// ── People ───────────────────────────────────────────────────────────────────

export const PEOPLE_HINT = "Owners change the initiative and are accountable for it. The sponsor backs it. Teams are the groups it belongs to; the status report lists it under each.";

/** The People tab, read view (Amendment 1). Pure, for the tests. */
export function PeopleView(props: { initiative: InitiativeDetail; onEdit?: () => void }): JSX.Element {
  const i = props.initiative;
  return (
    <div className="card">
      <div className="initCardHead">
        <h3>People</h3>
        {props.onEdit ? (
          <button type="button" className="btn btnSm" onClick={props.onEdit}>
            Edit people
          </button>
        ) : null}
      </div>
      <div className="initPeopleGrid">
        <section>
          <h4 className="initLabel">Owners</h4>
          <ul className="initPeopleList">
            {i.owners.map((o) => (
              <li key={o.account_id} className="initPerson">
                <span className="initAvatar" aria-hidden="true">
                  {initials(o.name)}
                </span>
                <span>
                  {o.name}
                  <small>Added {fmtDay(o.added_at)}</small>
                </span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h4 className="initLabel">Sponsor</h4>
          {i.sponsor ? (
            <ul className="initPeopleList">
              <li className="initPerson">
                <span className="initAvatar" aria-hidden="true">
                  {initials(i.sponsor.name)}
                </span>
                <span>{i.sponsor.name}</span>
              </li>
            </ul>
          ) : (
            <span className="muted" style={{ fontSize: 13 }}>
              None
            </span>
          )}
        </section>
        <section>
          <h4 className="initLabel">Teams</h4>
          {i.teams.length === 0 ? (
            <span className="muted" style={{ fontSize: 13 }}>
              None
            </span>
          ) : (
            <div className="initChips">
              {i.teams.map((t) => (
                <span key={t.id} className="pill pillNeutral">
                  {t.name}
                </span>
              ))}
            </div>
          )}
        </section>
      </div>
      <p className="initHint">{PEOPLE_HINT}</p>
    </div>
  );
}

function PeopleTab(props: { initiative: InitiativeDetail; candidates: MemberCandidate[]; onChanged: () => void }): JSX.Element {
  const toast = useToast();
  const i = props.initiative;
  const [editing, setEditing] = useState(false);
  const [owners, setOwners] = useState<string[]>([]);
  const [sponsor, setSponsor] = useState("");
  const [teams, setTeams] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const people = props.candidates.filter((c) => c.kind === "user");

  const start = () => {
    setOwners(i.owners.map((o) => o.account_id));
    setSponsor(i.sponsor?.account_id ?? "");
    setTeams(i.teams.map((t) => t.id));
    setError(null);
    setEditing(true);
  };

  async function save(): Promise<void> {
    if (owners.length === 0) {
      setError("An initiative keeps at least one owner.");
      return;
    }
    const before = new Set(i.owners.map((o) => o.account_id));
    const add = owners.filter((o) => !before.has(o));
    const remove = [...before].filter((o) => !owners.includes(o));
    const sponsorChanged = sponsor !== (i.sponsor?.account_id ?? "");
    const teamsChanged = JSON.stringify([...teams].sort()) !== JSON.stringify(i.teams.map((t) => t.id).sort());
    if (add.length === 0 && remove.length === 0 && !sponsorChanged && !teamsChanged) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    const base = `/api/tenant/initiatives/${encodeURIComponent(i.id)}`;
    try {
      if (add.length > 0 || remove.length > 0 || sponsorChanged) {
        await apiFetch(`${base}/owners`, {
          method: "PUT",
          body: { add, remove, ...(sponsorChanged ? { sponsor_account_id: sponsor || null } : {}) },
        });
      }
      if (teamsChanged) {
        await apiFetch(base, { method: "PATCH", body: { team_group_ids: teams } });
      }
      toast.push({ kind: "success", message: "Saved." });
      setEditing(false);
      props.onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      props.onChanged();
    } finally {
      setBusy(false);
    }
  }

  if (!editing) return <PeopleView initiative={i} onEdit={i.can_manage ? start : undefined} />;
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px" }}>
        <div className="initPeopleGrid">
          <div className="field">
            <span className="initLabel" style={{ margin: 0 }}>
              Owners
            </span>
            <MemberPicker mode="multi" allowedKinds={["user"]} candidates={props.candidates} selected={owners} onChange={setOwners} placeholder="Add an owner…" />
            <div className="help" style={error ? { color: "var(--danger-text)" } : undefined}>
              {error ?? "An initiative keeps at least one owner."}
            </div>
          </div>
          <label className="field">
            <span className="initLabel" style={{ margin: 0 }}>
              Sponsor
            </span>
            <select className="select" value={sponsor} onChange={(e) => setSponsor(e.target.value)}>
              <option value="">None</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </label>
          <div className="field">
            <span className="initLabel" style={{ margin: 0 }}>
              Teams
            </span>
            <MemberPicker mode="multi" allowedKinds={["group"]} candidates={props.candidates} selected={teams} onChange={setTeams} placeholder="Add a team…" />
          </div>
        </div>
      </div>
      <div className="initEditFooter">
        <button type="button" className="btn btnSm" disabled={busy} onClick={() => setEditing(false)}>
          Cancel
        </button>
        <button type="button" className="btn btnSm btnPrimary" disabled={busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ── the page ─────────────────────────────────────────────────────────────────

type Tab = "issues" | "log" | "measure" | "relations" | "people";
/** Spec §3.4: Issues · Log · Measure · Studies & reports · People; Issues is the default. */
const TABS: Array<{ key: Tab; label: string }> = [
  { key: "issues", label: "Issues" },
  { key: "log", label: "Log" },
  { key: "measure", label: "Measure" },
  { key: "relations", label: "Studies & reports" },
  { key: "people", label: "People" },
];

export function TenantInitiativeDetail(props: { entitlement?: string } = {}) {
  const { id = "" } = useParams<{ id: string }>();
  const [initiative, setInitiative] = useState<InitiativeDetail | null>(null);
  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<Tab>("issues");
  const [logKey, setLogKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, dir] = await Promise.all([
        apiFetch<{ initiative: InitiativeDetail }>(`/api/tenant/initiatives/${encodeURIComponent(id)}`),
        apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates").catch(() => ({ candidates: [] as MemberCandidate[] })),
      ]);
      setInitiative(detail.initiative);
      setCandidates(dir.candidates);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** A change elsewhere on the page re-reads the record (health, last activity, people) and the Log. */
  const refresh = useCallback(async () => {
    try {
      const r = await apiFetch<{ initiative: InitiativeDetail }>(`/api/tenant/initiatives/${encodeURIComponent(id)}`);
      setInitiative(r.initiative);
    } catch {
      /* the page keeps what it has; the next load shows the change */
    }
    setLogKey((k) => k + 1);
  }, [id]);

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;
  if (!initiative) return null;

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <Link className="breadcrumbLink" to="/app/initiatives">
            Initiatives
          </Link>
          <span className="breadcrumbSep">/</span>
          <span>{initiativeLabel(initiative)}</span>
        </span>
      }
      actions={
        initiative.can_manage && !editing ? (
          <button type="button" className="btn btnSm" onClick={() => setEditing(true)}>
            Edit
          </button>
        ) : undefined
      }
    >
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0, 1fr)" }}>
        {editing ? (
          <OverviewEditCard
            initiative={initiative}
            onCancel={() => setEditing(false)}
            onSaved={(next) => {
              setInitiative(next);
              setEditing(false);
              setLogKey((k) => k + 1);
            }}
          />
        ) : (
          <InitiativeOverviewCard initiative={initiative} />
        )}

        <div className="tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`tab${tab === t.key ? " tabActive" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "issues" ? <IssuesTab initiativeId={initiative.id} canRaise={props.entitlement === "builder" || props.entitlement === "admin"} refreshKey={logKey} /> : null}
        {tab === "log" ? (
          <LogTab
            initiativeId={initiative.id}
            refreshKey={logKey}
            canManage={initiative.can_manage}
            isAdmin={props.entitlement === "admin"}
            onChanged={() => {
              // The record's last activity and health move; the Log keeps what it has (the row is already in place).
              void apiFetch<{ initiative: InitiativeDetail }>(`/api/tenant/initiatives/${encodeURIComponent(id)}`)
                .then((r) => setInitiative(r.initiative))
                .catch(() => undefined);
            }}
          />
        ) : null}
        {tab === "measure" ? (
          <MeasureTab
            initiative={initiative}
            onChanged={(next) => {
              setInitiative(next);
              setLogKey((k) => k + 1);
            }}
          />
        ) : null}
        {tab === "relations" ? (
          <RelationsTab
            initiative={initiative}
            onChanged={(attached) => {
              setInitiative((prev) => (prev ? { ...prev, relations: { attached } } : prev));
              void refresh();
            }}
          />
        ) : null}
        {tab === "people" ? <PeopleTab initiative={initiative} candidates={candidates} onChanged={() => void refresh()} /> : null}
      </div>
    </PageLayout>
  );
}
